import * as React from 'react';
import { connect } from 'react-redux';

import PageSection from 'components/page/page-section/PageSection';
import Entity from 'components/entity/Entity';
import MediaPreview from 'components/MediaPreview';
import ProfileEntityName from 'components/entity/entity-name/ProfileEntityName';
import ProfileEntityAvatar from 'components/entity/entity-avatar/ProfileEntityAvatar';
import PromptExit from 'libs/router/prompt-exit/PromptExit';
import SortableTable from 'components/sortable-table/SortableTable';
import StatusLabel from 'components/status-label/StatusLabel';

import RealmOutfitDetails from '../realm-outfit-details/RealmOutfitDetails';
import RealmOutfitDetailsEdit from '../realm-outfit-details-edit/RealmOutfitDetailsEdit';
import RealmOutfitWMCLink from '../realm-outfit-wmc-link/RealmOutfitWMCLink';

import { hideConfirmModal, showConfirmModal } from 'redux-store/reducers/modal-reducer';
import { updateRealmOutfit } from 'modules/realm-outfits/reducers/realm-outfits';
import { fetchRealmOutfitsTags, resetRealmOutfitsTags } from 'modules/realm-outfits/reducers/realm-outfits-tags';

import { __t, applyTranslations } from 'utils/language';
import { getDisplayDate } from 'utils/date';
import {
  REALM_OUTFIT_STATUS_DECLINED,
  REALM_OUTFIT_STATUS_DRAFT,
  REALM_OUTFIT_STATUS_OFFLINE,
  REALM_OUTFIT_STATUS_PENDING,
  REALM_OUTFIT_STATUS_PUBLISHED,
  REALM_OUTFIT_STATUS_UNPUBLISHED
} from 'utils/realm-outfits';

import type { RealmOutfit, RealmOutfitForm, SortableTableColumnType } from 'types';

import './realm-outfits-table.scss';

applyTranslations(require('./admin-realm-outfits-table.i18n.json'));
applyTranslations(require('../realm-outfits-filters/admin-realm-outfits-filters.i18n.json'));

type OwnProps = {
  realmOutfits: Array<RealmOutfit>,
  realmOutfitsCount: number
};

type DispatchProps = {
  dispatchFetchRealmOutfitsTags: () => void,
  dispatchResetRealmOutfitsTags: () => void,
  dispatchShowConfirmationModal: (
    params: {
      message: string,
      confirmButtonLabel: string,
      onConfirm: () => void,
      onCancel: () => void
    }
  ) => void,
  dispatchHideConfirmationModal: () => void,
  dispatchUpdateRealmOutfit: (outfit: RealmOutfitForm) => Promise<void>
};

type Props = OwnProps & DispatchProps;

type State = {
  editedItemId: number
};

const OUTFIT_ID_NOT_EDITED = -1;

export const STATUS_LABEL_MAPPING = {
  [REALM_OUTFIT_STATUS_DRAFT]: 'draft',
  [REALM_OUTFIT_STATUS_PENDING]: 'warning',
  [REALM_OUTFIT_STATUS_PUBLISHED]: 'success',
  [REALM_OUTFIT_STATUS_DECLINED]: 'danger',
  [REALM_OUTFIT_STATUS_UNPUBLISHED]: 'neutral',
  [REALM_OUTFIT_STATUS_OFFLINE]: 'neutral'
};

const columns: Array<SortableTableColumnType> = [
  {
    title: __t('admin_realm_outfits_table.column.emOutfitId'),
    key: 'outfitId',
    render: (col) => col.emOutfitId || '—'
  },
  {
    title: __t('admin_realm_outfits_table.column.date_submit'),
    key: 'created_date',
    align: 'center',
    render: (col) => getDisplayDate(col.createdAt)
  },
  {
    title: __t('admin_realm_outfits_table.column.date_publish'),
    key: 'published_date',
    align: 'center',
    render: (col) => col.status === REALM_OUTFIT_STATUS_PUBLISHED && getDisplayDate(col.publishedFrom)
  },
  {
    title: __t('admin_realm_outfits_table.column.outfit'),
    key: 'outfit',
    render: col => (
      <MediaPreview
        attachment={{
          id: col.id,
          url: col.images[0]?.url,
          format: 'image'
        }}
        width={70}
        height={70}
      />
    )
  },
  {
    title: __t('admin_realm_outfits_table.column.items_count'),
    key: 'items_count',
    align: 'center',
    render: col => (col.items || []).length
  },
  {
    title: __t('admin_realm_outfits_table.column.status'),
    key: 'status',
    width: 110,
    render: col => (
      <StatusLabel
        label={__t(`admin_realm_outfits_filters.status_options.${col.status}`)}
        size="small"
        type={STATUS_LABEL_MAPPING[col.status]}
      />
    )
  },
  {
    title: __t('admin_realm_outfits_table.column.influencer'),
    key: 'influencer',
    render: (col) => (
      <Entity>
        <ProfileEntityAvatar
          profile={col.creatorProfile}
          bordered
        />
        <ProfileEntityName
          profile={col.creatorProfile}
          openInNewTab
        />
      </Entity>
    )
  },
  {
    title: __t('admin_realm_outfits_table.column.wmc_link'),
    key: 'wmc-link',
    width: 160,
    render: (col) => (
      <RealmOutfitWMCLink
        outfitLink={col.wmcShortLink}
        creatorOutfitsLink={col?.creatorProfile?.wmcShortLink}
      />
    )
  }
];

class RealmOutfitsTable extends React.PureComponent<Props, State> {
  state = {
    editedItemId: OUTFIT_ID_NOT_EDITED
  };

  componentDidMount() {
    const { dispatchFetchRealmOutfitsTags } = this.props;
    dispatchFetchRealmOutfitsTags();
  }

  componentWillUnmount() {
    const { dispatchResetRealmOutfitsTags } = this.props;
    dispatchResetRealmOutfitsTags();
  }

  isCollapsible = () => true;

  isEditMode() {
    return this.state.editedItemId !== OUTFIT_ID_NOT_EDITED;
  }

  handleCancel = () => {
    this.handleEdit(OUTFIT_ID_NOT_EDITED);
  };

  handleEdit = (id: number) => {
    if (this.isEditMode()) {
      const {
        dispatchShowConfirmationModal,
        dispatchHideConfirmationModal
      } = this.props;

      dispatchShowConfirmationModal({
        message: __t('admin_realm_outfits_table.outfit_edit_confirmation.message'),
        confirmButtonLabel: __t('admin_realm_outfits_table.outfit_edit_confirmation.button_discard'),
        onConfirm: () => {
          this.setState({
            editedItemId: id
          }, () => dispatchHideConfirmationModal());
        },
        onCancel: () => dispatchHideConfirmationModal()
      });
    } else {
      this.setState({
        editedItemId: id
      });
    }
  };

  handleSave = (outfit: RealmOutfitForm) => {
    const { dispatchUpdateRealmOutfit } = this.props;

    dispatchUpdateRealmOutfit(outfit).then(() => {
      this.setState({
        editedItemId: OUTFIT_ID_NOT_EDITED
      });
    });
  };

  renderCollapsedContent = (outfit: RealmOutfit) => {
    const { editedItemId } = this.state;

    if (outfit.id === editedItemId) {
      return (
        <RealmOutfitDetailsEdit
          outfit={outfit}
          onCancel={this.handleCancel}
          onSave={this.handleSave}
        />
      );
    }

    return (
      <RealmOutfitDetails
        outfit={outfit}
      />
    );
  };

  render() {
    const { realmOutfits, realmOutfitsCount } = this.props;
    const { editedItemId } = this.state;

    return (
      <PageSection
        padding="none"
        title={`${String(realmOutfitsCount)} ${__t('admin_realm_outfits_table.count')}`}
        customClass="realm-outfits-table"
        titleProps={{
          color: 'normal'
        }}
      >
        { this.isEditMode() && <PromptExit type="page" /> }
        <SortableTable
          data={realmOutfits}
          expandedRows={editedItemId ? [editedItemId] : undefined}
          columns={columns}
          noScroll
          isCollapsible={this.isCollapsible}
          renderCollapsedContent={this.renderCollapsedContent}
        />
      </PageSection>
    );
  }
}

const mapDispatchToProps = (dispatch): DispatchProps => ({
  dispatchFetchRealmOutfitsTags: () => dispatch(fetchRealmOutfitsTags()),
  dispatchResetRealmOutfitsTags: () => dispatch(resetRealmOutfitsTags()),
  dispatchShowConfirmationModal: (params) => dispatch(showConfirmModal(params)),
  dispatchHideConfirmationModal: () => dispatch(hideConfirmModal()),
  dispatchUpdateRealmOutfit: (outfit) => dispatch(updateRealmOutfit(outfit))
});

export default connect(null, mapDispatchToProps)(RealmOutfitsTable);
